import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import {
  DialogType, DialogPositioningBehavior, DialogProps, HorizontalOriginOption, VerticalOriginOption, DialogWidget
} from '@tableau/widgets';
import React from 'react';

import { Formatters, Messages } from '../../../../../gen-src/Localize';
import { DataQualityCertificationAssetFragment, DataQualityWarningFragment, DataQualityWarningAssetFragment } from '../../../../../gen-src/queries';
import { SiteUrlsProvider, SiteUrlsProviderProps } from '../DataCatalogProvider';
import { localizeDataQualityTypeName, stringToDataQualityType } from '../dataQualityWarnings/DataQualityType';
import { usePrevious } from '../hooks/usePrevious';
import { useTelemetrySender } from '../hooks/useTelemetrySender';
import { useToggle } from '../hooks/useToggle';
import { LabelsTelemetryEvent } from '../labels/LabelsTelemetryEvent';
import { IndicatorDetailPanelDialogBodyStyle, IndicatorPanelDialogStyle } from '../style/DataQualityIndicatorStyles';
import { noopSiteUrls, SiteUrlsContext } from '../urls/SiteUrlsContext';
import { assertUnreachable } from '../utils/TypescriptHelpers';
import { DataQualityCertificationInteractiveButtonOrIcon, DataQualityWarningInteractiveButton, DataQualityWarningInteractiveIcon } from './DataQualityIndicatorButtons';
import { RenderAssetsIndicators } from './DataQualityIndicatorDetail';
import { mapRenderCertificationsToIndicatorProps, mapRenderWarningsToIndicatorsProps, CertificationStatusProps, WarningStatusProps, AssetMarkedByIndicator } from './DataQualityIndicatorFragments';
import { DataQualityDetailPanelSide, DataQualityWarningKind, getTooltipAnchorClientRect, RenderIndicatorsProps } from './DataQualityIndicatorSharedHelpers';

type UsePanelStateParams<P> = {
  readonly testId: string;
  readonly props: P & { readonly source?: string };
} & Pick<LabelsTelemetryEvent, 'category'>;

function usePanelState<T, P extends CertificationStatusProps | WarningStatusProps>({ testId, category, props }: UsePanelStateParams<P>) {
  const { source } = props;
  const ref = React.useRef<T>(null);
  const [isDialogOpen, toggleDialogOpened] = useToggle();
  const wasDialogOpened = usePrevious(isDialogOpen);

  const emit = useTelemetrySender<LabelsTelemetryEvent>({ type: 'label_opened', category, source });

  const onClick = React.useCallback((props: P) => {
    if (!wasDialogOpened.current) {
      const event = getTelemetryEvent(props);
      emit({ ...event });
    }

    toggleDialogOpened();
  }, [emit, wasDialogOpened, toggleDialogOpened])

  const closeDialog = React.useCallback(() => toggleDialogOpened(false), [toggleDialogOpened]);

  return {
    isDialogOpen, onClick, closeDialog, testId, ref
  }
}

function getTelemetryEvent(
  props: CertificationStatusProps | WarningStatusProps
): Omit<LabelsTelemetryEvent, 'type' | 'source' | 'category'> {
  return {
    content_type: getContentTypeFromPanelStateParams(props.anchoredAsset),
    elevated: getElevatedFromPanelStateParams(props),
    value: getValueFromPanelStateParams(props),
  }
}

function extractSevere(item: IDataQualityIndicator | DataQualityWarningFragment): boolean {
  if ('__typename' in item) {
    return item.isSevere;
  }

  if ('isSevere' in item) {
    return item.isSevere;
  }

  return false;
}

function extractValue(item: IDataQualityIndicator | DataQualityWarningFragment): string {
  if ('__typename' in item) {
    return item.warningType?.toLowerCase();
  }

  if ('type' in item) {
    return item.type?.toLowerCase();
  }

  return 'unknown';
}

function getElevatedFromPanelStateParams(props: CertificationStatusProps | WarningStatusProps): boolean {
  if ('anchoredCertifications' in props) {
    return false
  }

  if ('anchoredWarnings' in props) {
    switch (props.kind) {
      case 'graph': return props.anchoredWarnings.some(extractSevere);
      case 'vizportal': return props.anchoredWarnings.some(extractSevere);
    }
  }

  return false;
}

function getValueFromPanelStateParams(props: CertificationStatusProps | WarningStatusProps): string {
  if ('anchoredCertifications' in props) {
    return 'certification'
  }

  const valueList: string[] = []
  if ('anchoredWarnings' in props) {
    valueList.push(...props.anchoredWarnings.map(extractValue))
    valueList.push(...props.upstreamWarnings.map(extractValue))
  }

  if ('anchoredChildWarnings' in props && props.anchoredChildWarnings) {
    valueList.push(...props.anchoredChildWarnings.map(extractValue))
  }

  if (valueList.length === 0) {
    return 'unknown'
  }

  return valueList.join(',')
}

function getContentTypeFromPanelStateParams(content: null | DataQualityCertificationAssetFragment | DataQualityWarningAssetFragment | AssetMarkedByIndicator): string {
  if (!content) {
    return 'unknown';
  }

  if ('__typename' in content) {
    return content.asset?.__typename ?? 'unknown';
  }

  if ('assetType' in content) {
    return content.assetType ?? 'unknown';
  }

  return 'unknown';
}

function shouldSkipWarningDueToNoIndicators({ anchoredChildWarnings, upstreamWarnings, ...rest }: WarningStatusProps): boolean {
  const hasNoChildWarnings = (anchoredChildWarnings ?? []).filter(t => t.isActive).length < 1
  const hasNoUpstreamWarnings = upstreamWarnings.filter(t => t.isActive).length < 1
  switch (rest.kind) {
    case 'graph': return rest.anchoredWarnings.filter(t => t.isActive).length < 1 && hasNoUpstreamWarnings && hasNoChildWarnings;
    case 'vizportal': return rest.anchoredWarnings.filter(t => t.isActive).length < 1 && hasNoUpstreamWarnings && hasNoChildWarnings;
    default:
      assertUnreachable(rest);
      return true;
  }
}

function shouldSkipCertificationDueToNoIndicators(props: CertificationStatusProps): boolean {
  switch (props.kind) {
    case 'graph': return props.anchoredCertifications.filter(t => t.isActive).length < 1;
    case 'vizportal': return props.anchoredCertifications.filter(t => t.isActive).length < 1;
    default:
      assertUnreachable(props);
      return true;
  }
}

const DataQualityCertificationStatusChipId = 'data-quality-certification-chip-id';

/**
 * @deprecated TFS-1478812
*/
export function EmbeddableCertificationStatusChipOrIcon(props: CertificationStatusProps & SiteUrlsProviderProps) {
  const { siteUrls, ...rest } = props;

  return (
    <SiteUrlsProvider siteUrls={siteUrls}>
      <CertificationStatusChipOrIcon {...rest} />
    </SiteUrlsProvider >
  );
}

/**
 * @deprecated TFS-1478812
*/
export function CertificationStatusChipOrIcon(props: CertificationStatusProps): JSX.Element | null {
  const { isDialogOpen, onClick, closeDialog, ref, ...rest } = usePanelState<HTMLButtonElement, CertificationStatusProps>({
    testId: DataQualityCertificationStatusChipId,
    category: 'certification',
    props,
  });

  if (shouldSkipCertificationDueToNoIndicators(props)) {
    return null;
  }

  const title = certificationTooltipTitle(props);
  const indicators = mapRenderCertificationsToIndicatorProps(props);

  return (
    <SiteUrlsContext.Provider value={props.urls || noopSiteUrls}>
      <DataQualityCertificationInteractiveButtonOrIcon {...rest} onClick={() => { onClick(props) }} iconOnly={props.iconOnly} ref={ref} tabIndex={props.tabIndex} />
      {
        isDialogOpen &&
        <DataQualityIndicatorDetailPanel
          indicators={indicators}
          onCloseRequest={closeDialog}
          side={props.side || 'right'}
          title={title}
          anchorEl={ref.current as HTMLElement}
          stackingRootId={props.stackingRootId}
        />
      }
    </SiteUrlsContext.Provider>
  );
}

const DataQualityWarningStatusChipId = 'data-quality-warning-chip-id';

function buildWarningTooltipAndPanelProps(props: WarningStatusProps) {
  const warningKind = getWarningKind(props);
  const title = warningTooltipTitle(props);
  const indicators = mapRenderWarningsToIndicatorsProps(props);

  return { warningKind, title, indicators }
}

/**
 * @deprecated TFS-1478812
*/
export function EmbeddableWarningStatusIcon(props: WarningStatusProps & SiteUrlsProviderProps) {
  const { siteUrls, ...rest } = props;

  return (
    <SiteUrlsProvider siteUrls={siteUrls}>
      <WarningStatusIcon {...rest} />
    </SiteUrlsProvider >
  );
}

/**
 * @deprecated TFS-1478812
*/
export function WarningStatusIcon(props: WarningStatusProps): JSX.Element | null {
  const { isDialogOpen, onClick, closeDialog, ref, ...rest } = usePanelState<HTMLButtonElement, WarningStatusProps>({
    testId: DataQualityWarningStatusChipId,
    category: 'warning',
    props,
  });

  if (shouldSkipWarningDueToNoIndicators(props)) {
    return null;
  }

  const { warningKind, title, indicators } = buildWarningTooltipAndPanelProps(props);

  return (
    <SiteUrlsContext.Provider value={props.urls || noopSiteUrls}>
      <DataQualityWarningInteractiveIcon {...rest} onClick={() => { onClick(props) }} warningKind={warningKind} title={title} ref={ref} tabIndex={props.tabIndex} />
      {
        isDialogOpen &&
        <DataQualityIndicatorDetailPanel
          indicators={indicators}
          onCloseRequest={closeDialog}
          side={props.side || 'right'}
          title={title}
          anchorEl={ref.current as HTMLElement}
          stackingRootId={props.stackingRootId}
        />
      }
    </SiteUrlsContext.Provider>
  )
}

/**
 * @deprecated TFS-1478812
*/
export function EmbeddableWarningStatusChip(props: WarningStatusProps & SiteUrlsProviderProps) {
  const { siteUrls, ...rest } = props;

  return (
    <SiteUrlsProvider siteUrls={siteUrls}>
      <WarningStatusChip {...rest} />
    </SiteUrlsProvider >
  );
}

/**
 * @deprecated TFS-1478812
*/
export function WarningStatusChip(props: WarningStatusProps): JSX.Element | null {
  const { isDialogOpen, onClick, closeDialog, ref, ...rest } = usePanelState<HTMLDivElement, WarningStatusProps>({
    testId: DataQualityWarningStatusChipId,
    category: 'warning',
    props,
  });

  if (shouldSkipWarningDueToNoIndicators(props)) {
    return null;
  }

  const { warningKind, title, indicators } = buildWarningTooltipAndPanelProps(props);

  return (
    <SiteUrlsContext.Provider value={props.urls || noopSiteUrls}>
      <DataQualityWarningInteractiveButton {...rest} onClick={() => { onClick(props) }} warningKind={warningKind} title={title} ref={ref} tabIndex={props.tabIndex} />
      {
        isDialogOpen &&
        <DataQualityIndicatorDetailPanel
          indicators={indicators}
          onCloseRequest={closeDialog}
          side={props.side || 'right'}
          title={title}
          anchorEl={ref.current as HTMLElement}
          stackingRootId={props.stackingRootId}
        />
      }
    </SiteUrlsContext.Provider>
  )
}

function certificationTooltipTitle(props: CertificationStatusProps): string {
  if (props.anchoredCertifications.length <= 0) {
    return '';
  }

  switch (props.kind) {
    case 'vizportal':
      return localizeDataQualityTypeName(stringToDataQualityType(props.anchoredCertifications[0].type));
    case 'graph':
      return localizeDataQualityTypeName(stringToDataQualityType(props.anchoredCertifications[0].__typename));
    default:
      assertUnreachable(props);
      return '';
  }
}

function warningTooltipTitle(props: WarningStatusProps): string {
  const count = props.anchoredWarnings.length + props.upstreamWarnings.length + (props.kind === 'graph' ? props.anchoredChildWarnings?.length ?? 0 : 0);

  if (count <= 0) {
    return '';
  }

  if (count === 1) {
    if (props.anchoredWarnings.length === 1) {
      switch (props.kind) {
        case 'vizportal':
          return localizeDataQualityTypeName(stringToDataQualityType(props.anchoredWarnings[0].type));
        case 'graph':
          return localizeDataQualityTypeName(stringToDataQualityType(props.anchoredWarnings[0].warningType));
        default:
          assertUnreachable(props);
          return '';
      }
    }
  }

  return Messages.DataQualityIconMessage({
    MESSAGE_COUNT: count,
    MESSAGE_COUNT_LOC: Formatters.formatCount(count)
  });
}

function getWarningKind(props: WarningStatusProps): DataQualityWarningKind {
  switch (props.kind) {
    case 'graph': {
      if (props.anchoredWarnings.some(w => w.isSevere) || props.anchoredChildWarnings?.some(w => w.isSevere)) {
        return 'severe-warning';
      }
      break;
    }
    case 'vizportal': {
      if (props.anchoredWarnings.some(w => w.isSevere)) {
        return 'severe-warning';
      }
      break;
    }
    default:
      assertUnreachable(props);
  }

  return (props.upstreamWarnings.some(w => w.isSevere)) ? 'severe-warning' : 'standard-warning';
}

type DataQualityIndicatorDetailPanelProps = {
  readonly indicators: RenderIndicatorsProps;
  readonly title: string;
  readonly anchorEl?: HTMLElement;
  readonly onCloseRequest: () => void;
  readonly side: DataQualityDetailPanelSide;
  readonly stackingRootId?: string;
}

function DataQualityIndicatorDetailPanel(props: DataQualityIndicatorDetailPanelProps): JSX.Element {
  const siteUrls = React.useContext(SiteUrlsContext);

  const dialogProps: DialogProps = {
    title: props.title || '',
    getAnchorClientRect: () => getTooltipAnchorClientRect(props.anchorEl),
    positioningBehavior: DialogPositioningBehavior.Anchored,
    dialogStyle: IndicatorPanelDialogStyle,
    dialogBodyStyle: IndicatorDetailPanelDialogBodyStyle,
    anchorOrigin: {
      vertical: VerticalOriginOption.top,
      horizontal:
        props.side === 'right'
          ? HorizontalOriginOption.right
          : HorizontalOriginOption.left,
    },
    showFooter: false,
    shouldAdjustPositionOnWindowResize: true,
    testId: 'data-quality-detail-panel',
    isModal: true,
    isGlassTransparent: true,
    type: DialogType.Uber,
    showCloseButton: false,
    handleGlassClick: props.onCloseRequest,
    onCloseRequest: props.onCloseRequest,
    shouldReturnFocusOnClose: false,
    disableAutoFocus: true,
    stackingRootId: props.stackingRootId,
  };

  const { linksDisabled, ...rest } = props.indicators;

  return (
    <DialogWidget {...dialogProps}>
      <SiteUrlsContext.Provider value={siteUrls}>
        <RenderAssetsIndicators {...rest} linksDisabled={linksDisabled ?? false} />
      </SiteUrlsContext.Provider>
    </DialogWidget>
  );
}
